import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { LangProvider, LANGS } from "@/i18n/LangContext";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import Home from "@/pages/Home";
import Wizard from "@/pages/Wizard";
import Confirmation from "@/pages/Confirmation";
import AdminLogin from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import LeadDetail from "@/pages/admin/LeadDetail";
import Leads from "@/pages/admin/Leads";
import Analytics from "@/pages/admin/Analytics";
import System from "@/pages/admin/System";
import Marketing from "@/pages/admin/Marketing";
import Inbox from "@/pages/admin/Inbox";
import AuthCallback from "@/pages/admin/AuthCallback";
import ServiceArea from "@/pages/ServiceArea";
import AdminReviews from "@/pages/admin/AdminReviews";
import Signature from "@/pages/admin/Signature";
import { initCallTracking } from "@/lib/callTracking";

const PATHS = {
  home: { nl: "/", fr: "/fr", en: "/en", es: "/es" },
  wizard: { nl: "/aanvraag", fr: "/fr/demande", en: "/en/request", es: "/es/solicitud" },
  confirm: { nl: "/bedankt", fr: "/fr/merci", en: "/en/thank-you", es: "/es/gracias" },
};

const langRoutes = (lang) => [
  <Route key={`${lang}-home`} path={PATHS.home[lang]} element={<LangProvider lang={lang}><Home /></LangProvider>} />,
  <Route key={`${lang}-wiz`} path={PATHS.wizard[lang]} element={<LangProvider lang={lang}><Wizard /></LangProvider>} />,
  <Route key={`${lang}-conf`} path={PATHS.confirm[lang]} element={<LangProvider lang={lang}><Confirmation /></LangProvider>} />,
];

function AppRoutes() {
  const location = useLocation();
  // Detect Google OAuth return synchronously during render (prevents race conditions)
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {LANGS.map((lang) => langRoutes(lang))}
      <Route path="/schilder/:gemeente" element={<LangProvider lang="nl"><ServiceArea /></LangProvider>} />
      <Route path="/fr/peintre/:gemeente" element={<LangProvider lang="fr"><ServiceArea /></LangProvider>} />
      <Route path="/en/painter/:gemeente" element={<LangProvider lang="en"><ServiceArea /></LangProvider>} />
      <Route path="/es/pintor/:gemeente" element={<LangProvider lang="es"><ServiceArea /></LangProvider>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/admin/system" element={<ProtectedRoute><System /></ProtectedRoute>} />
      <Route path="/admin/lead/:id" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
      <Route path="/admin/marketing" element={<ProtectedRoute><Marketing /></ProtectedRoute>} />
      <Route path="/admin/reviews" element={<ProtectedRoute><AdminReviews /></ProtectedRoute>} />
      <Route path="/admin/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
      <Route path="/admin/signature" element={<ProtectedRoute><Signature /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.1, anchors: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    const stopCallTracking = initCallTracking();
    return () => {
      stopCallTracking();
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
